import React, { Fragment, memo, useContext, ReactNode } from "react";
import { GridLegacy as Grid, GridSize, Select, useMediaQuery, useTheme } from "@mui/material";
import { FiAlertTriangle } from "react-icons/fi";
import { ReactDatePickerProps } from "react-datepicker";

import { EditableGroupContext } from "../EditableGroup/EditableGroup";
import FieldLabel from "./components/FieldLabel";
import Viewable from "./components/Viewable";
import Editable from "./components/Editable";
import FloatingInputLabel from "./components/FloatingInputLabel";

import SwitchField from "./fields/SwitchField";
import DateField from "./fields/DateField";
import PortalDatePicker from "./fields/PortalDatePicker"
import TimeField from "./fields/TimeField";
import DVLAField from "./fields/DVLAField";
import TextInputField, { CharacterCasing } from "./fields/TextInputField";
import NumberInputField from "./fields/NumberInputField";
import TerminalField from "./fields/TerminalField";
import DropDownField, { IDropDownListItem } from "./fields/DropDownField";
import LocationField from "./fields/LocationField";
import MoneyInputField from "./fields/MoneyInputField";
import PackageField from "./fields/PackageField";
import DistributionListField from "./fields/DistributionList/DistributionListField";
import MultiSelectField from "./fields/MultiSelectField";

import { formatCurrency, formatPercentage } from "../../../utility/utils";
import { SessionContext } from "../../../utility/useSession";
import { useDate, isValidDate } from '../../../utility/useDate';

import flex from "../../../styles/flex";

import { AssetLocationTypes, Category, Terminal } from "../../../logic/api";
import PortalDateTime from "./fields/PortalDateTime";
import AddLabel from "./components/AddLabel";
import Icon from "./components/Icon";
import { useTranslation } from "react-i18next";
import PhoneNumberInputField from "./fields/PhoneNumberInputField";
import parsePhoneNumberFromString from "libphonenumber-js";
import { isSupportedCountry } from "react-phone-number-input";
import {
  ReleaseFlag,
  useReleaseManagement,
} from "../../../utility/feature-management";


export enum EditableFieldTypes {
  text = 0,
  number = 1,
  label = 2,
  switch = 3,
  date = 4,
  time = 5,
  dvla = 6,
  title = 7,
  terminal = 8,
  empty = 9,
  password = 10,
  terminalField = 11,
  locationLane = 12,
  locationZone = 13,
  locationLocker = 15,
  textMultiLine = 16,
  textMultiLineFull = 17,
  money = 18,
  package = 19,
  textSingle = 20,
  image = 21,
  percentage = 22,
  distributionList = 23,
  enumDropDownField = 24,
  datePicker = 25,
  dateTimePicker = 26,
  addLabel = 27,
  icon = 28,
  multiSelect = 29,
  phoneNumber = 30,
}

type BaseEditableField<G, T> = {
  label: string;
  viewableStyle?: React.CSSProperties;
  onProvide?: (data: G) => T;
  onChange?: (data: G, value: T) => void;
  onValidate?: (data: G, value: T) => string | null;
  onRequestData?: (data: G) => Promise<IDropDownListItem[]>;
  onRequestText?: (data: G) => string;
  datePickerProps?: ReactDatePickerProps;
  readOnly?: boolean;
  required?: boolean;
  alwaysVisible?: boolean;
  showValueWhenReadOnly?: boolean;
  useFullWidthWhenViewing?: boolean; // TODO: this is a temp fix for the booking fields, we need to find a better solution/refactor the code
  useFullWidthWhenEditing?: boolean;
  showCharacterCount?: boolean;
  maxLength?: number | null;
  maxWidth?: number | string;
  casing?: CharacterCasing;
  testId?: string;
  iconButton?: ReactNode;
  onEnterKeyPress?: (value: string) => void;
  onBlur?: () => void;
  defaultValue?: string;
  date?: Date;
  hideReadOnlyWhenEditing?: boolean;
  onClick?: () => void;
  columnWidth?: GridSize;
  placeholder?: string;
  copyable?: boolean;
};

// Conditional types to ensure each inputType only allows its specific props
export type EditableField<G, T> = BaseEditableField<G, T> & (
  | {
    inputType: EditableFieldTypes.multiSelect;
    limitTags?: number; // Only multiSelect can limit visible chips
    defaultMinDate?: never;
    initializeFilled?: never;
  }
  | {
    inputType: EditableFieldTypes.dateTimePicker;
    limitTags?: never;
    defaultMinDate?: boolean; // Only dateTimePicker can set min date
    initializeFilled?: boolean; // Only dateTimePicker can auto-fill
  }
  | {
    inputType: Exclude<EditableFieldTypes, EditableFieldTypes.multiSelect | EditableFieldTypes.dateTimePicker>;
    // Other types cannot use these props, so we need to explicitly set them to never
    limitTags?: never;
    defaultMinDate?: never;
    initializeFilled?: never;
  }
);

interface IFieldProps {
  readOnly: boolean;
  useFullWidthWhenEditing: boolean;
  alwaysVisible: boolean;
  showValueWhenReadOnly: boolean;
}

export const EditableFieldProps = React.createContext<Partial<IFieldProps>>({});

const CloneObject = (obj: unknown) => {
  return JSON.parse(JSON.stringify(obj));
};

function component<G, T>(props: React.PropsWithChildren<EditableField<G, T>>) {
  const state = useContext(EditableGroupContext);
  const { t } = useTranslation();
  const editing = state.isEditing;
  const theme = useTheme();
  const session = useContext(SessionContext);
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("xs"));
  const { toFormattedDateTimeString } = useDate();
  const useE164 = useReleaseManagement(
    ReleaseFlag.releaseMobileNumberTruncatesFix
  );
  const defaultCountryCodePref = session.current.site?.preferences.default_country_code ?? "";
  const defaultCountryCode = isSupportedCountry(defaultCountryCodePref)
    ? defaultCountryCodePref
    : undefined;



  let validationError: string | null = null;

  let fullWidthInput = props.useFullWidthWhenViewing || false;
  let hideInput = false;

  if (
    editing == true &&
    props.readOnly == true &&
    props.inputType == EditableFieldTypes.empty
  ) {
    return <Fragment></Fragment>;
  }

  if (editing != true && props.inputType == EditableFieldTypes.time) {
    return <Fragment></Fragment>;
  }

  if (props.useFullWidthWhenEditing == true && editing == true) {
    fullWidthInput = true;
  }

  if (isSmallDevice == true) {
    fullWidthInput = true;
  }

  if (props.inputType == EditableFieldTypes.textMultiLineFull) {
    fullWidthInput = true;
  }

  if (props.hideReadOnlyWhenEditing == true && props.readOnly == true && editing == true) {
    hideInput = true;
  }

  const label = `${props.label}${editing && props.required ? " *" : ""}`;

  let value: unknown;

  if (props.onProvide != null) {
    value = props.onProvide(state.data as G);
    if (props.onValidate) {
      validationError = props.onValidate(state.data as G, value as T);
    }
  }

  const setChange = (value: unknown, rebuild?: boolean) => {
    if (props.onChange != null) {
      const data = CloneObject(state.data);
      props.onChange(data as G, value as T);
      if (state.setData) {
        state.setData(data, rebuild);
      }
    }
  };

  const toDateString = (input: unknown) => {
    if (typeof input !== 'string') {
      return '';
    }

    const date = new Date(input);
    if (!isValidDate(date)) {
      return '';
    }

    return toFormattedDateTimeString(date);
  };

  let textString = "";

  if (props.onRequestText != null) {
    textString = props.onRequestText(state.data as G);
  }

  return (
    <EditableFieldProps.Provider
      value={{
        readOnly: props.readOnly == true,
        useFullWidthWhenEditing: fullWidthInput,
        alwaysVisible: props.alwaysVisible,
        showValueWhenReadOnly: props.showValueWhenReadOnly == true,
      }}
    >
      <Grid item xs={12} sm={
        typeof props.columnWidth === "number" && props.columnWidth >= 1 && props.columnWidth <= 12
          ? props.columnWidth
          : props.columnWidth === "auto"
            ? "auto"
            : 6
      } style={{ display: hideInput ? 'none' : '' }}>
        {props.inputType == EditableFieldTypes.label && (
          <div style={flex.flexColumns}>
            <FieldLabel alwaysVisible={true}>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>{value as string}</div>
          </div>
        )}

        {validationError != null && (
          <div style={flex.flexColumns}>
            <div style={{ ...flex.flexFill, color: "red" }}>
              <div style={{ textAlign: "center" }}>
                <FiAlertTriangle color="red"></FiAlertTriangle>{" "}
                {validationError}
              </div>
            </div>
          </div>
        )}

        {props.inputType == EditableFieldTypes.title && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <FloatingInputLabel label={label}>
                  <Select
                    native
                    style={{
                      width: "100%",
                      maxWidth: 225,
                      borderRadius: 5,
                      padding: 5,
                    }}
                    value={value as string}
                    onChange={(e) => {
                      setChange(e.target.value);
                    }}
                  >
                    <option value={""} title=""></option>
                    <option value={"MR"} title="MR">
                      {t("editable.mr")}
                    </option>
                    <option value={"MRS"} title="MRS">
                      {t("editable.mrs")}
                    </option>
                    <option value={"MS"} title="MS">
                      {t("editable.ms")}
                    </option>
                    <option value={"MISS"} title="MISS">
                      {t("editable.miss")}
                    </option>
                    <option value={"DR"} title="DR">
                      {t("editable.dr")}
                    </option>
                  </Select>
                </FloatingInputLabel>
              </Editable>
              <Viewable>{value as string}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.terminalField && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <DropDownField
                  readOnly={props.readOnly}
                  label={label}
                  selectedItem={value as IDropDownListItem}
                  onRequestData={async () => {
                    if (props.onRequestData == null) {
                      return [];
                    }
                    return props.onRequestData(state.data as G);
                  }}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></DropDownField>
              </Editable>
              <Viewable>{value != null && (value as Terminal).name}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.enumDropDownField && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <DropDownField
                  readOnly={props.readOnly}
                  label={label}
                  maxWidth={props.maxWidth}
                  selectedItem={value as IDropDownListItem}
                  onRequestData={async () => {
                    if (props.onRequestData == null) {
                      return [];
                    }
                    return props.onRequestData(state.data as G);
                  }}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></DropDownField>
              </Editable>

              <Viewable style={props.viewableStyle}>{textString || (value as IDropDownListItem)?.name || ''}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.distributionList && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <DistributionListField
                  label={label}
                  value={value as Category}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></DistributionListField>
              </Editable>
              <Viewable>{value != null && (value as Category).categoryName}{value == null && "NONE"}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.locationLane && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <LocationField
                  label={label}
                  value={value as string}
                  onChange={(value) => setChange(value)}
                  displayTypes={[
                    AssetLocationTypes.Zone,
                    AssetLocationTypes.Lane,
                  ]}
                  selectionTypes={[AssetLocationTypes.Lane]}
                  testId={props.testId}
                ></LocationField>
              </Editable>
              <Viewable>{value != null && (value as Terminal).name}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.locationZone && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <LocationField
                  label={label}
                  value={value as string}
                  onChange={(value) => setChange(value)}
                  displayTypes={[AssetLocationTypes.Zone]}
                  selectionTypes={[AssetLocationTypes.Zone]}
                  testId={props.testId}
                ></LocationField>
              </Editable>
              <Viewable>{value != null && (value as Terminal).name}</Viewable>
            </div>
          </div>
        )}

        {props.inputType == EditableFieldTypes.locationLocker && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <LocationField
                  label={label}
                  value={value as string}
                  onChange={(value) => setChange(value)}
                  displayTypes={[AssetLocationTypes.KeyLocker]}
                  selectionTypes={[AssetLocationTypes.KeyLocker]}
                  testId={props.testId}
                ></LocationField>
              </Editable>
              <Viewable>{value != null && (value as Terminal).name}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.terminal && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <TerminalField
                  label={label}
                  value={value as Terminal}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></TerminalField>
              </Editable>
              <Viewable>{value != null && (value as Terminal).name}</Viewable>
            </div>
          </div>
        )}

        {props.inputType == EditableFieldTypes.switch && (
          <div
            style={{
              ...flex.flexColumns,
              textAlign: isSmallDevice ? "center" : "left",
            }}
          >
            {label && <FieldLabel>{label}</FieldLabel>}
            <div style={flex.flexFill}>
              <SwitchField
                testId={props.testId}
                label={editing != true && label ? null : label}
                readOnly={editing != true || props.readOnly == true}
                value={value == true}
                onChange={(checked) => setChange(checked)}
              />
            </div>
          </div>
        )}

        {props.inputType == EditableFieldTypes.image && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <TextInputField
                  maxLength={props.maxLength}
                  casing={props.casing}
                  label={label}
                  value={value as string}
                  readOnly={props.readOnly}
                  onChange={(value) => setChange(value)}
                ></TextInputField>
              </Editable>
              <Viewable>
                <div>
                  {session.current.site != null &&
                    session.current.site.preferences.company_logo_base64 !=
                    null && (
                      <div
                        style={{
                          backgroundColor: "black",
                          padding: 10,
                          borderRadius: 5,
                        }}
                      >
                        <img
                          width="100%"
                          src={
                            `data:image/png;base64,${session.current.site.preferences.company_logo_base64}`
                          }
                        />
                      </div>
                    )}
                </div>
              </Viewable>
            </div>
          </div>
        )}

        {props.inputType == EditableFieldTypes.text && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <TextInputField
                  maxLength={props.maxLength}
                  maxWidth={props.maxWidth}
                  casing={props.casing}
                  label={label}
                  value={value as string}
                  readOnly={props.readOnly}
                  showCharacterCount={props.showCharacterCount}
                  onChange={(value) => setChange(value)}
                  onBlur={props.onBlur}
                  iconButton={props.iconButton}
                  onEnterKeyPress={props.onEnterKeyPress}
                  testId={props.testId}
                  placeholder={props.placeholder}
                />
              </Editable>
              <Viewable
                style={props.viewableStyle}
                copyable={props.copyable} // For now only text fields are copyable
                fieldLabel={props.label}
              >
                {value as string}
              </Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.textSingle && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <TextInputField
                  label={label}
                  maxLength={1}
                  showCharacterCount={props.showCharacterCount}
                  value={value as string}
                  readOnly={props.readOnly}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></TextInputField>
              </Editable>
              <Viewable>{value as string}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.package && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <PackageField
                  label={label}
                  value={value as string}
                  readOnly={props.readOnly}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></PackageField>
              </Editable>
              <Viewable>{value as string}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.textMultiLine && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <TextInputField
                  multiLine={true}
                  label={label}
                  value={value as string}
                  showCharacterCount={props.showCharacterCount}
                  maxWidth={props.maxWidth}
                  maxLength={props.maxLength}
                  readOnly={props.readOnly}
                  onChange={(value) => setChange(value)}
                  placeholder={props.placeholder}
                  testId={props.testId}
                ></TextInputField>
              </Editable>
              <Viewable>{value as string}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.textMultiLineFull && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <TextInputField
                  multiLine={true}
                  label={label}
                  value={value as string}
                  readOnly={props.readOnly}
                  maxLength={props.maxLength}
                  maxWidth={500}
                  onChange={(value) => setChange(value)}
                  placeholder={props.placeholder}
                  testId={props.testId}
                ></TextInputField>
              </Editable>
              <Viewable allowOverflow={true}>{value as string}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.password && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <TextInputField
                  label={label}
                  password={true}
                  readOnly={props.readOnly}
                  value={value as string}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></TextInputField>
              </Editable>
              <Viewable>*******</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.number && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <NumberInputField
                  label={label}
                  readOnly={props.readOnly}
                  value={value as number}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></NumberInputField>
              </Editable>
              <Viewable>{value as string}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.money && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <MoneyInputField
                  label={label}
                  readOnly={props.readOnly}
                  value={value as number}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></MoneyInputField>
              </Editable>
              <Viewable>{formatCurrency(value as string, session)}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.percentage && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <MoneyInputField
                  label={label}
                  readOnly={props.readOnly}
                  value={value as number}
                  onChange={(value) => setChange(value)}
                  testId={props.testId}
                ></MoneyInputField>
              </Editable>
              <Viewable>{value == null ? "0" : formatPercentage(value)}</Viewable>
            </div>
          </div>
        )}
        {props.inputType == EditableFieldTypes.dvla && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <DVLAField
                  label={label}
                  value={value as string}
                  onChange={(value) => setChange(value, true)}
                  testId={props.testId}
                ></DVLAField>
              </Editable>
              <Viewable>{value as string}</Viewable>
            </div>
          </div>
        )}

        {props.inputType == EditableFieldTypes.date && (
          <div style={flex.flexColumns}>
            <FieldLabel alwaysVisible={props.readOnly}>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <DateField
                  label={label}
                  value={value as string}
                  onChange={(val) => setChange(val)}
                  testId={props.testId}
                  readOnly={props.readOnly}
                />
              </Editable>
              <Viewable>{toDateString(value)}</Viewable>
            </div>
          </div>
        )}

        {props.inputType == EditableFieldTypes.datePicker && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <PortalDatePicker
                  label={label}
                  selected={props.datePickerProps?.selected}
                  minDate={props?.datePickerProps?.minDate}
                  onChange={(val: Date) => {
                    setChange(val);
                  }}
                  testId={props.testId}
                  readOnly={props.readOnly}
                />
              </Editable>
              <Viewable>{toDateString(value)}</Viewable>
            </div>
          </div>
        )}

        {props.inputType == EditableFieldTypes.time && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <TimeField
                  label={label}
                  value={value as string}
                  onChange={(val) => setChange(val)}
                  testId={props.testId}
                  readOnly={props.readOnly}
                />
              </Editable>
              <Viewable>{toDateString(value)}</Viewable>
            </div>
          </div>
        )}

        {props.inputType == EditableFieldTypes.dateTimePicker && (
          <>
            <FieldLabel>{label}</FieldLabel>
            <Editable>
              <PortalDateTime
                label={label}
                value={value as string}
                testId={props.testId}
                readOnly={props.readOnly}
                defaultMinDate={props.defaultMinDate}
                onChange={(val: string) => {
                  setChange(val);
                }}
                timeProps={{
                  value: value as string,
                  label: label + t("editable.time"),
                  onChange: () => ""
                }}
                dateProps={{
                  value: value as string,
                  label: label + t("editable.date"),
                  initializeFilled: props.initializeFilled,
                  selected: props.datePickerProps?.selected,
                  maxDate: props.datePickerProps?.maxDate,
                  minDate: props.datePickerProps?.minDate,
                  onChange: () => ""
                }}
              />
            </Editable>
            <Viewable>{toDateString(value)}</Viewable>
          </>
        )}

        {props.inputType == EditableFieldTypes.addLabel && (
          <div style={flex.flexColumns}>
            <Editable>
              {props.onClick && <AddLabel
                label={label}
                onClick={props.onClick}
              />}
            </Editable>
          </div>
        )}

        {props.inputType == EditableFieldTypes.icon && (
          <div style={flex.flexColumns}>
            <Editable>
              {props.onClick && <Icon
                label={label}
                onClick={props.onClick}
              >{props.children}</Icon>}
            </Editable>
          </div>
        )}
        {props.inputType == EditableFieldTypes.multiSelect && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <MultiSelectField
                  label={label}
                  readOnly={props.readOnly}
                  selectedItems={value as IDropDownListItem[]}
                  onRequestData={async () => {
                    if (props.onRequestData == null) {
                      return [];
                    }
                    return props.onRequestData(state.data as G);
                  }}
                  onChange={(value) => setChange(value)}
                  maxWidth={props.maxWidth}
                  testId={props.testId}
                  placeholder={props.placeholder}
                  limitTags={props.limitTags}
                />
              </Editable>
              <Viewable>
                {(value as IDropDownListItem[])?.map(item => item.name).join(', ') || ''}
              </Viewable>
            </div>
          </div>
        )}
        {props.inputType === EditableFieldTypes.phoneNumber && (
          <div style={flex.flexColumns}>
            <FieldLabel>{label}</FieldLabel>
            <div style={{ ...flex.flexFill }}>
              <Editable>
                <PhoneNumberInputField
                  label={label}
                  value={value as string}
                  defaultCountryCode={session.current.site?.preferences?.default_country_code || "GB"}
                  readOnly={props.readOnly}
                  required={props.required}
                  placeholder={props.placeholder}
                  onChange={(val) => setChange(val)}
                  testId={props.testId}
                />
              </Editable>

              <Viewable>
                {(() => {
                  const phone = value as string;
                  if (!phone) return "-";

                  try {
                    const parsed = parsePhoneNumberFromString(phone, defaultCountryCode);
                    if (!useE164) {
                      return parsed?.formatNational() ?? phone;;
                    }
                    return parsed?.isValid() ? parsed.format('E.164') : phone;
                  } catch {
                    return phone;
                  }
                })()}
              </Viewable>
            </div>
          </div>
        )}
      </Grid>
    </EditableFieldProps.Provider>
  );
}

export const EditableField = memo(component) as typeof component;

export default EditableField;
